<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('stockConfirm.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          { text: $t('stockConfirm.pageTitle'), href: '/admin/stock-confirm' },
          {
            text: $t('stockConfirm.editSerial'),
            active: true,
          },
        ]"
      />
    </div>
		<ts-card>
			<ts-card-wrapper>
				<ts-loading-banner :loading="loading">
					<div class="ts-relative ts-w-full">
						<input
							v-model.trim="search"
							ref="search"
							type="text"
							class="
								ts-w-full
								ts-border
								ts-float-right
								ts-px-2
								ts-py-1
								ts-rounded
								ts-outline-none
								ts-pr-6
							"
							:placeholder="$t('stockConfirm.searchProductList')"
						/>
						<span
							class="ts-absolute ts-right-0 ts-mt-1 ts-mr-2"
							style="margin-top: 7px"
							><i class="fa fa-search"></i
						></span>
        	</div>
					<div style="clear:both"></div>
					<div class="max-h ts-mt-3">
						<table class="ts-w-full">
								<thead>
									<tr>
										<th class="ts-border ts-border-gray-300 ts-uppercase ts-px-2 ts-py-2 ts-whitespace-no-wrap">{{ $t('stockConfirm.serial') }}</th>
										<th class="ts-border ts-border-gray-300 ts-uppercase ts-px-2 ts-py-2 ts-whitespace-no-wrap">{{ $t('stockConfirm.barcode') }}</th>
										<th class="ts-border ts-border-gray-300 ts-uppercase ts-px-2 ts-py-2 ts-whitespace-no-wrap">{{ $t('stockConfirm.productName') }}</th>
										<th class="ts-border ts-border-gray-300 ts-uppercase ts-px-2 ts-py-2 ts-whitespace-no-wrap">{{ $t('stockConfirm.orgSerial') }}</th>
										<th class="ts-border ts-border-gray-300 ts-uppercase ts-px-2 ts-py-2 ts-whitespace-no-wrap">{{ $t('stockConfirm.vinNumber') }}</th>
										<th class="ts-border ts-border-gray-300 ts-uppercase ts-px-2 ts-py-2 ts-whitespace-no-wrap">{{ $t('stockConfirm.engineNumber') }}</th>
										<th class="ts-border ts-border-gray-300 ts-uppercase ts-px-2 ts-py-2 ts-whitespace-no-wrap">{{ $t('stockConfirm.grade') }}</th>
										<th class="ts-border ts-border-gray-300 ts-uppercase ts-px-2 ts-py-2 ts-whitespace-no-wrap">{{ $t('stockConfirm.remark') }}</th>
									</tr>
								</thead>
								<tbody>
									<tr
										class="ts-border-b ts-border-gray-300"
										v-for="(s, index) in itemList"
										:key="index"
									>
										<td class="ts-px-2 ts-py-2 ts-w-48">
											<input type="text" 
												v-model.trim="s.serial_no"
												class="ts-bg-blue-200 ts-py-2 ts-px-2 ts-outline-none ts-font-extrabold ts-w-full"
												:class="{ 'is-invalid': errors.has('serial_list.' + index + '.serial_no') }" 
												readonly
											>
											<div
												class="invalid-feedback ts-text-xs"
												v-if="errors.has('serial_list.' + index + '.serial_no')"
											></div>
										</td>
										<td class="ts-w-40 ts-p-2">
											<input type="text" class="ts-bg-gray-200 ts-w-full ts-py-2 ts-px-2 ts-outline-none"
												:value="s.barcode"
												readonly
											>
										</td>
										<td class="ts-w-56">
											<input type="text" class="ts-bg-gray-200 ts-w-full ts-py-2 ts-px-2 ts-outline-none"
												:value="s.product_name_en"
												readonly
											>
										</td>
										<td class="ts-w-56">
											<input
												v-model.trim="s.original_serial"
												:placeholder="$t('stockConfirm.required')" 
												type="text"
												class="ts-px-2 ts-py-2 ts-outline-none ts-w-full ts-bg-gray-200"
												:class="{ 'ts-border ts-border-red-400': errors.has('serial_list.' + index + '.original_serial') }" 
											>
										</td>
										<td class="ts-w-40">
											<input
												v-model.trim="s.imei"  
												type="text"
												class="ts-px-2 ts-py-2 ts-outline-none ts-bg-gray-200 ts-w-full"
												:class="{ 'ts-border ts-border-red-400': errors.has('serial_list.' + index + '.imei') }"
											>
										</td>
										<td class="ts-w-40">
											<input
												v-model.trim="s.engine_number" 
												type="text"
												class="ts-px-2 ts-py-2 ts-outline-none ts-bg-gray-200 ts-w-full"
												:class="{ 'ts-border ts-border-red-400': errors.has('serial_list.' + index + '.engine_number') }"
											>
										</td>
										<td class="ts-w-16">
											<select 
												v-model="s.product_grade"
												class="ts-px-2 ts-py-2 ts-outline-none ts-bg-gray-200 ts-w-full"
												:class="{ 'ts-border ts-border-red-400': errors.has('serial_list.' + index + '.product_grade') }"
											>
												<option value="A">A</option>
												<option value="B">B</option>
												<option value="C">C</option>
												<option value="D">D</option>
											</select>
										</td>
										<td>
											<input
												v-model.trim="s.qc_remarks"   
												type="text"
												class="ts-px-2 ts-py-2 ts-outline-none ts-bg-gray-200 ts-w-full"
												:class="{ 'ts-border ts-border-red-400': errors.has('serial_list.' + index + '.qc_remarks') }"
											>
										</td>
									</tr>
								</tbody>
						</table>
					</div>
					<div class="ts-flex ts-justify-end ts-space-x-3 ts-mt-3 ts-border-t ts-border-gray-400 ts-pt-3">
						<ts-button @click.prevent="$router.push({ name: 'stock-confirm'})">{{ $t('cancel') }}</ts-button>
						<ts-button 
							color="primary"
							:waiting="waiting"
							@click.prevent="onUpdate"
						>{{ $t('update') }}</ts-button>
					</div>
				</ts-loading-banner>
			</ts-card-wrapper>
		</ts-card>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions } from 'vuex'
export default {
	name: 'editSerial',
	data() {
		return {
			errors: new Errors(),
			loading: false,
			waiting: false,
			search: '',
			model:{
				serial_list: []
			}
		}
	},
	computed: {
		itemList() {
			return this.model.serial_list.filter(p => 
				this.search.split(' ').every((s) => p.product_name_en.toLowerCase().includes(s.toLowerCase()))  ||
        this.search.split(' ').every((s) => p.barcode.toLowerCase().includes(s.toLowerCase())) 
			) 
		}
	},
	methods: {
		...mapActions('stock/stockInConfirm', ['getSerial', 'updateSerial']),
		fetchData(){
			this.loading = true
			this.model.serial_list = []
			this.getSerial(
				this.$route.params.stockId
			)
			.then(response => {
				this.model.serial_list = response.data
			})
			.catch(error => {
				this.$toasted.error(error.message)
			})
			.finally(() => {
				this.loading = false
			})
		},
		onUpdate(){
			this.waiting = true
			let { barcode, product_name_en, ...data} = this.model
			this.updateSerial(data)
				.then(response => {
					this.$toasted.success(response.message)
					this.$router.push({ name: 'stock-confirm'})
				})
				.catch(error => {
					this.$toasted.error(error.message)
					this.errors = new Errors(error.errors)
				})
				.finally(() => {
					this.waiting = false
				})
		}
	},
	beforeRouteEnter(to, from, next) {
		next(vm => {
			vm.fetchData()
		})
	}
}
</script>

<style scoped>
.max-h {
  height: 600px;
  max-height: 610px;
  overflow: scroll;
}
table {
  /* white-space: nowrap; */
  margin: 0;
  /* border-collapse: separate; */
  border-spacing: 0;
  /* table-layout: fixed; */
  /* border: 1px solid black; */
}
table thead {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: rgb(22, 102, 145);
  color: white;
}
</style>